// The quotation as edited on screen (text boxes keep what is typed) and its conversion to the save body.
import type { CostType, E2IConfig, Quotation, QuotationLine, QuotationSaveInput } from '../../types/e2i';
import type { AdjustMode, QuoteFigures } from '../../utils/e2i';
import { adjustOf, dateOrNull, numberText, parseNumber, quotationFigures, repriceLine } from '../../utils/e2i';
import { vatRateOf } from './workspace';

export interface QuoteDraft {
  /** Markup % per cost type, as typed. */
  markups: Record<string, string>;
  profit: string;
  chargeMode: AdjustMode;
  chargeValue: string;
  chargeReason: string;
  discountMode: AdjustMode;
  discountValue: string;
  vatCode: string;
  validUntil: string;
  terms: string;
  scope: string;
  clientRef: string;
  lines: QuotationLine[];
}

export function draftOfQuotation(q: Quotation, costTypes: CostType[]): QuoteDraft {
  const markups: Record<string, string> = {};
  const codes = new Set<string>([...costTypes.map((c) => c.code), ...(q.lines || []).map((l) => l.costType || '').filter(Boolean)]);
  codes.forEach((code) => {
    const own = q.markups?.[code];
    markups[code] = numberText(own !== undefined && own !== null ? Number(own) : Number(costTypes.find((c) => c.code === code)?.markup) || 0);
  });
  const charge = adjustOf(q.addChargePct, q.addChargeAmount);
  const discount = adjustOf(q.discountPct, q.discountAmount);
  return {
    markups,
    profit: numberText(q.profitPct ?? 0),
    chargeMode: charge.mode,
    chargeValue: numberText(charge.value),
    chargeReason: q.addChargeReason || '',
    discountMode: discount.mode,
    discountValue: numberText(discount.value),
    vatCode: q.vatCode || '',
    validUntil: q.validUntil ? String(q.validUntil).slice(0, 10) : '',
    terms: q.terms || '',
    scope: q.scope || '',
    clientRef: q.clientRef || '',
    lines: (q.lines || []).map((l) => ({ ...l })),
  };
}

/** The typed markups as numbers (what is not a number counts as 0 in the preview). */
export const markupNumbers = (d: QuoteDraft): Record<string, number> => {
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(d.markups)) out[k] = parseNumber(v) ?? 0;
  return out;
};

export const profitNumber = (d: QuoteDraft): number => parseNumber(d.profit) ?? 0;

/** Every cost line priced again with the draft's markups and profit; priced lines keep their price. */
export const repriceDraft = (d: QuoteDraft, costTypes: CostType[]): QuoteDraft => {
  const m = markupNumbers(d);
  const p = profitNumber(d);
  return { ...d, lines: d.lines.map((l) => repriceLine(l, m, p, costTypes)) };
};

export const draftFigures = (d: QuoteDraft, config: E2IConfig, fallbackRate: number): QuoteFigures =>
  quotationFigures({
    lines: d.lines,
    chargeMode: d.chargeMode,
    chargeValue: parseNumber(d.chargeValue),
    discountMode: d.discountMode,
    discountValue: parseNumber(d.discountValue),
    vatRate: vatRateOf(config, d.vatCode, fallbackRate),
  });

/** What stops a save; `forSubmit` adds what approval needs. */
export function draftProblems(d: QuoteDraft, config: E2IConfig, forSubmit: boolean): string[] {
  const out: string[] = [];
  const used = new Set(d.lines.map((l) => l.costType || '').filter(Boolean));
  used.forEach((code) => {
    const v = d.markups[code];
    if (v !== undefined && parseNumber(v) === null) out.push(`The ${code} markup is not a number`);
  });
  if (parseNumber(d.profit) === null) out.push('The profit is not a number');
  if (d.chargeMode !== 'none') {
    const v = parseNumber(d.chargeValue);
    if (v === null || v < 0) out.push('The extra charge is not an amount');
    if (!d.chargeReason.trim()) out.push('Give the reason for the extra charge');
  }
  if (d.discountMode !== 'none') {
    const v = parseNumber(d.discountValue);
    if (v === null || v < 0) out.push('The discount is not an amount');
    if (d.discountMode === 'pct' && v !== null && v > 100) out.push('The discount cannot be above 100%');
  }
  if (d.validUntil.trim() && !dateOrNull(d.validUntil)) out.push('The validity date is not a date');
  const bad = d.lines.filter((l) => !l.description.trim() || !(Number(l.quantity) > 0) || Number(l.unitPrice) < 0).length;
  if (bad) out.push(`${bad} line${bad === 1 ? ' is' : 's are'} incomplete`);
  if (forSubmit) {
    if (!d.lines.length) out.push('Add at least one priced line');
    if (config.vatCodes.length && !d.vatCode) out.push('Choose the VAT code');
  }
  return out;
}

const orNull = (s: string): string | null => (s.trim() ? s.trim() : null);

export function saveInputOf(d: QuoteDraft): QuotationSaveInput {
  const charge = parseNumber(d.chargeValue);
  const discount = parseNumber(d.discountValue);
  return {
    markups: markupNumbers(d),
    profitPct: profitNumber(d),
    addChargePct: d.chargeMode === 'pct' ? charge : null,
    addChargeAmount: d.chargeMode === 'amount' ? charge : null,
    addChargeReason: d.chargeMode !== 'none' ? orNull(d.chargeReason) : null,
    discountPct: d.discountMode === 'pct' ? discount : null,
    discountAmount: d.discountMode === 'amount' ? discount : null,
    vatCode: d.vatCode || null,
    validUntil: dateOrNull(d.validUntil),
    terms: orNull(d.terms),
    scope: orNull(d.scope),
    clientRef: orNull(d.clientRef),
    lines: d.lines.map((l, i) => ({ ...l, lineNum: i + 1, description: l.description.trim() })),
  };
}
